/**
 * Order Validation Utilities
 * 
 * Provides validation functions for store orders according to the user flow requirements.
 * Ensures all required fields are filled and delivery date is in the future.
 */

import { GlobalOrderData } from '../hooks/useGlobalOrderData';

export interface OrderValidationResult {
  isValid: boolean;
  errors: {
    poNumber?: string;
    deliveryDate?: string;
    orderType?: string;
    products?: string;
  };
  message?: string;
}

/**
 * Validates if an order is ready for preview
 * 
 * Requirements:
 * - PO Number: Optional
 * - Delivery Date: Optional (but if provided, must be a future date)
 * - Order Type: Required
 * - At least one product selected: Required
 * - For Cust Order (store-order only): Minimum 150 items total quantity required
 * - Special Instructions: Optional (can be empty)
 * 
 * @param orderData - The order data to validate
 * @param formType - Type of form: 'store-order' or 'supplies' (default: 'store-order')
 * @returns Validation result with errors if any
 */
export const validateOrderForPreview = (orderData: GlobalOrderData, formType: 'store-order' | 'supplies' = 'store-order'): OrderValidationResult => {
  const errors: OrderValidationResult['errors'] = {};
  const issues: string[] = [];

  // PO Number is now optional - no validation needed

  // Validate Delivery Date (optional, but if provided must be in the future)
  if (orderData.deliveryDate && orderData.deliveryDate.trim() !== '') {
    const deliveryDate = new Date(orderData.deliveryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
    
    if (deliveryDate <= today) {
      errors.deliveryDate = 'Delivery Date must be a future date';
      issues.push('Delivery Date (must be future date)');
    }
  }

  // Validate Order Type
  if (!orderData.orderType || orderData.orderType === null) {
    errors.orderType = 'Order Type is required';
    issues.push('Order Type');
  }

  // Validate at least one product is selected
  // Check if this is a supplies order (has supplyItems but no product items) or store order
  const hasProductItems = (orderData.heritageItems && orderData.heritageItems.length > 0) ||
                          (orderData.livingstonItems && orderData.livingstonItems.length > 0) ||
                          (orderData.flowerItems && orderData.flowerItems.length > 0) ||
                          (orderData.vegetableItems && orderData.vegetableItems.length > 0);
  const hasSupplyItems = orderData.supplyItems && orderData.supplyItems.length > 0;
  
  // Determine order type: if only supplyItems exist, it's a supplies order; otherwise it's a store order
  const isSuppliesOrder = hasSupplyItems && !hasProductItems;
  
  let totalProducts = 0;
  if (isSuppliesOrder) {
    // For supplies orders, only count supply items
    totalProducts = (orderData.supplyItems || []).reduce((sum, item) => sum + item.quantity, 0);
  } else {
    // For store orders, only count product items (exclude supplies)
    totalProducts = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ].reduce((sum, item) => sum + item.quantity, 0);
  }

  if (totalProducts === 0) {
    errors.products = 'At least one product must be selected';
    issues.push('at least one product');
  }

  // Validate minimum 150 items for Cust Order (store-order only, not supplies)
  if (formType === 'store-order' && orderData.orderType === 'custOrder' && totalProducts > 0) {
    if (totalProducts < 150) {
      errors.products = `Cust Order requires a minimum of 150 items. Current total: ${totalProducts} items`;
      issues.push(`minimum 150 items for Cust Order (currently ${totalProducts})`);
    }
  }

  // Build validation message
  let message: string | undefined;
  if (issues.length > 0) {
    message = `Fill out the necessary fields to proceed: ${issues.join(', ')}`;
  }

  return {
    isValid: issues.length === 0,
    errors,
    message
  };
};

/**
 * Validates delivery date is in the future
 * 
 * @param deliveryDate - The delivery date string to validate
 * @returns True if date is in the future, false otherwise
 */
export const isDeliveryDateValid = (deliveryDate: string): boolean => {
  if (!deliveryDate || deliveryDate.trim() === '') {
    return false;
  }

  const date = new Date(deliveryDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day

  return date > today;
};

/**
 * Gets a user-friendly validation message for the Preview Order button
 * 
 * @param orderData - The order data to validate
 * @param formType - Type of form: 'store-order' or 'supplies' (default: 'store-order')
 * @returns A message explaining what fields are missing
 */
export const getPreviewButtonValidationMessage = (orderData: GlobalOrderData, formType: 'store-order' | 'supplies' = 'store-order'): string => {
  const validation = validateOrderForPreview(orderData, formType);
  
  if (validation.isValid) {
    return '';
  }

  return validation.message || 'Fill out the necessary fields to proceed';
};

